import React, { useEffect, useRef, useState } from 'react';
import { useStore } from '../store/useStore';
import { usePuffApi } from '../hooks/usePuffApi';
import { getSmokingUnit } from '@puff/shared';
import { MotivationCard } from '../components/MotivationCard';
import { HealthTimeline } from '../components/HealthTimeline';
import { theme } from '../theme';

export const QuitPlan: React.FC = () => {
  const { user, todayCount } = useStore();
  const { refreshStats } = usePuffApi();
  const unit = getSmokingUnit(user?.smokingType);

  const [weeklyAvg, setWeeklyAvg] = useState<number | null>(null);
  const [hasZeroDay, setHasZeroDay] = useState(false);
  const roadmapRef = useRef<HTMLDivElement>(null);
  const currentWeekRef = useRef<HTMLDivElement>(null);

  const startingAmount = user?.dailyAmount || 20;
  const costPerPuff = user?.costPerPuff || 0.5;

  // Reduction plan calculations
  const reductionPerWeek = Math.max(1, Math.round(startingAmount * 0.1));
  const totalWeeks = Math.ceil(startingAmount / reductionPerWeek);

  const daysSinceStart = (() => {
    const start = user?.createdAt ? new Date(user.createdAt) : new Date();
    return Math.max(0, Math.floor((Date.now() - start.getTime()) / (1000 * 60 * 60 * 24)));
  })();
  const weekNumber = Math.floor(daysSinceStart / 7) + 1;

  // Generate roadmap
  const roadmap = Array.from({ length: totalWeeks + 1 }, (_, i) => {
    const target = Math.max(0, Math.round(startingAmount - (i + 1) * reductionPerWeek));
    return {
      week: i + 1,
      target,
      isCurrent: i + 1 === weekNumber,
      isPast: i + 1 < weekNumber,
    };
  });
  // Ensure last entry is 0
  if (roadmap.length > 0) roadmap[roadmap.length - 1].target = 0;

  const currentWeekTarget = roadmap.find(r => r.isCurrent)?.target
    ?? Math.max(0, Math.round(startingAmount - weekNumber * reductionPerWeek));

  const overallProgress = Math.min(100, Math.round(
    (weekNumber - 1) / totalWeeks * 100
  ));

  // Fetch weekly stats
  useEffect(() => {
    if (!user?.id) return;
    refreshStats('week').then(stats => {
      if (stats?.dailyStats) {
        const values = Object.values(stats.dailyStats) as number[];
        if (values.length > 0) {
          const avg = values.reduce((a, b) => a + b, 0) / values.length;
          setWeeklyAvg(Math.round(avg * 10) / 10);
          setHasZeroDay(values.some(v => v === 0));
        }
      }
    }).catch(() => {});
  }, [user?.id]);

  // Document title
  useEffect(() => {
    document.title = 'แผนลดบุหรี่';
    return () => { document.title = 'porddee - ตัวช่วยเลิกสูบ'; };
  }, []);

  // Auto-scroll roadmap to current week
  useEffect(() => {
    if (currentWeekRef.current && roadmapRef.current) {
      const container = roadmapRef.current;
      const el = currentWeekRef.current;
      const offset = el.offsetTop - container.offsetTop - 40;
      container.scrollTo({ top: offset, behavior: 'smooth' });
    }
  }, [weekNumber]);

  const todayOk = todayCount <= currentWeekTarget;
  const avgOk = weeklyAvg !== null && weeklyAvg <= currentWeekTarget;

  // Dynamic achievements
  const achievements = [
    {
      icon: '🚀', title: 'เริ่มต้นการเปลี่ยนแปลง',
      desc: 'สมัครใช้แอปเรียบร้อย', done: true,
    },
    {
      icon: '📉', title: 'ลดได้ 25%',
      desc: `เฉลี่ย ≤ ${Math.round(startingAmount * 0.75)} ${unit}/วัน`,
      done: weeklyAvg !== null && weeklyAvg <= startingAmount * 0.75,
    },
    {
      icon: '🔥', title: 'ลดได้ 50%',
      desc: `เฉลี่ย ≤ ${Math.round(startingAmount * 0.5)} ${unit}/วัน`,
      done: weeklyAvg !== null && weeklyAvg <= startingAmount * 0.5,
    },
    {
      icon: '⭐', title: 'ลดได้ 75%',
      desc: `เฉลี่ย ≤ ${Math.round(startingAmount * 0.25)} ${unit}/วัน`,
      done: weeklyAvg !== null && weeklyAvg <= startingAmount * 0.25,
    },
    {
      icon: '🏆', title: 'ไม่สูบเลย 1 วัน',
      desc: 'มีวันที่สูบ 0 ใน 7 วันที่ผ่านมา',
      done: hasZeroDay,
    },
  ];

  // Money saved estimate (compared to starting amount)
  const moneySavedPerDay = Math.max(0, startingAmount - (weeklyAvg ?? todayCount)) * costPerPuff;

  return (
    <div className="page" style={{ background: '#f8fafc', minHeight: '100dvh' }}>
      <div style={{ padding: '20px 20px 40px' }}>

        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', marginBottom: 20 }}>
          แผนลดบุหรี่ 🎯
        </h1>

        {/* ── 1. PROGRESS OVERVIEW ── */}
        <div style={{
          background: `linear-gradient(135deg, ${theme.primaryLight} 0%, ${theme.primary} 100%)`,
          borderRadius: 24, padding: 24, marginBottom: 16,
          color: 'white', overflow: 'hidden',
          boxShadow: `0 8px 24px rgba(${theme.primaryRgb}, 0.3)`,
        }}>
          <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 4 }}>
            สัปดาห์ที่ {weekNumber} ของการลด
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, marginBottom: 12 }}>
            {startingAmount} → 0 {unit}/วัน
          </div>

          {/* Progress bar */}
          <div style={{
            background: 'rgba(255,255,255,0.2)', borderRadius: 10, height: 12,
            marginBottom: 8, overflow: 'hidden',
          }}>
            <div style={{
              width: `${overallProgress}%`, height: '100%',
              background: 'white', borderRadius: 10,
              transition: 'width 0.5s ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, opacity: 0.8 }}>
            <span>ผ่านมาแล้ว {overallProgress}%</span>
            <span>{daysSinceStart} วัน</span>
          </div>
        </div>

        {/* ── 2. THIS WEEK CARD ── */}
        <div style={{
          background: 'white', borderRadius: 20, padding: 20, marginBottom: 16,
          border: '2px solid #f1f5f9',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
              เป้าหมายสัปดาห์นี้
            </div>
            <div style={{
              padding: '4px 12px', borderRadius: 20,
              background: todayOk ? '#ecfdf5' : '#fffbeb',
              color: todayOk ? '#10b981' : '#f59e0b',
              fontSize: 13, fontWeight: 700,
            }}>
              {todayOk ? 'อยู่ในเป้า ✅' : 'เกินเป้า ⚠️'}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            {/* Target */}
            <div style={{ flex: 1, background: '#f8fafc', borderRadius: 16, padding: '14px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>เป้า/วัน</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>{currentWeekTarget}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{unit}</div>
            </div>
            {/* Today */}
            <div style={{ flex: 1, background: '#f8fafc', borderRadius: 16, padding: '14px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>วันนี้</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: todayOk ? '#10b981' : '#f59e0b' }}>{todayCount}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{unit}</div>
            </div>
            {/* Weekly avg */}
            <div style={{ flex: 1, background: '#f8fafc', borderRadius: 16, padding: '14px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>เฉลี่ย 7 วัน</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: avgOk ? '#10b981' : weeklyAvg !== null ? '#f59e0b' : '#94a3b8' }}>
                {weeklyAvg !== null ? weeklyAvg : '—'}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{unit}</div>
            </div>
          </div>

          {/* Money saved estimate */}
          {moneySavedPerDay > 0 && (
            <div style={{
              marginTop: 12, padding: '10px 14px', borderRadius: 12,
              background: '#ecfdf5', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 18 }}>💰</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#059669' }}>
                ประหยัดได้ ~฿{Math.round(moneySavedPerDay)}/วัน เทียบกับตอนเริ่ม
              </span>
            </div>
          )}
        </div>

        {/* ── 3. REDUCTION ROADMAP ── */}
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
            แผนการลด
          </h3>
          <div ref={roadmapRef} style={{
            background: 'white', borderRadius: 20, padding: '4px 16px',
            border: '2px solid #f1f5f9',
            maxHeight: 280, overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}>
            {roadmap.map((r, i) => (
              <div key={i} ref={r.isCurrent ? currentWeekRef : undefined} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 0',
                borderBottom: i < roadmap.length - 1 ? '1px solid #f1f5f9' : 'none',
                opacity: r.isPast && !r.isCurrent ? 0.5 : 1,
              }}>
                {/* Indicator */}
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: r.isPast ? '#10b981' : r.isCurrent ? theme.primary : '#e2e8f0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: 12, fontWeight: 700,
                  boxShadow: r.isCurrent ? `0 0 0 4px rgba(${theme.primaryRgb}, 0.2)` : 'none',
                }}>
                  {r.isPast ? '✓' : r.target === 0 ? '🎯' : r.week}
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: 14, fontWeight: r.isCurrent ? 700 : 500,
                    color: r.isCurrent ? '#0f172a' : '#64748b',
                  }}>
                    สัปดาห์ {r.week}{r.isCurrent ? ' (ปัจจุบัน)' : ''}
                  </div>
                </div>

                <div style={{
                  fontSize: 14, fontWeight: 700,
                  color: r.isCurrent ? theme.primary : r.isPast ? '#10b981' : '#94a3b8',
                }}>
                  {r.target === 0 ? 'เลิกสูบ! 🎉' : `${r.target} ${unit}/วัน`}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── 4. ACHIEVEMENTS ── */}
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
            ความสำเร็จ
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {achievements.map((a, i) => (
              <div key={i} style={{
                background: 'white', borderRadius: 16, padding: '14px 16px',
                display: 'flex', alignItems: 'center', gap: 12,
                border: a.done ? '2px solid #d1fae5' : '2px solid #f1f5f9',
                opacity: a.done ? 1 : 0.5,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12,
                  background: a.done ? '#ecfdf5' : '#f8fafc',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20,
                }}>
                  {a.done ? a.icon : '🔒'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: a.done ? '#0f172a' : '#94a3b8' }}>
                    {a.title}
                  </div>
                  <div style={{ fontSize: 12, color: a.done ? '#64748b' : '#cbd5e1' }}>
                    {a.desc}
                  </div>
                </div>
                {a.done && <span style={{ fontSize: 16 }}>✅</span>}
              </div>
            ))}
          </div>
        </div>

        {/* ── 5. MOTIVATION CARD ── */}
        <MotivationCard />

        {/* ── 6. HEALTH TIMELINE (for users with quitDate) ── */}
        <HealthTimeline />

      </div>
    </div>
  );
};
